import * as React from "react";
import * as d3 from "d3";
import Device from "../../enums/Device";
import getInitialViewTranslation from "../../utils/map/getInitialViewTranslation";
import Scale from "../../types/Scale";
import WDMap from "../map/WDMap";
import useViewport from "../../hooks/useViewport";
import getDevice from "../../utils/getDevice";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import {
  gameApiSliceActions,
  gameOrdersMeta,
} from "../../state/game/game-api-slice";
import { Unit } from "../../utils/map/getUnits";
import { IOrderDataHistorical } from "../../models/Interfaces";
import GameStateMaps from "../../state/interfaces/GameStateMaps";
import { APITerritories } from "../../state/interfaces/GameDataResponse";
import { getActiveMapVariant } from "../../map/variants";
import { StandoffInfo } from "../map/components/WDArrowContainer";

const Scales: Scale = {
  DESKTOP: [0.45, 3],
  MOBILE_LG: [0.32, 1.6],
  MOBILE_LG_LANDSCAPE: [0.3, 1.6],
  MOBILE: [0.32, 1.6],
  MOBILE_LANDSCAPE: [0.27, 1.6],
  TABLET: [0.6275, 3],
  TABLET_LANDSCAPE: [0.6, 3],
};

const getInitialScaleForDevice = (device: Device): number[] => {
  return Scales[device];
};

/*
 * 地图上下两端会被浮动 UI 盖住：底部是补给数 + 阶段条（约 120px），
 * 顶部是阶段/操作提示。原来平移范围被锁死在 [0,0]–[W,H]，贴边的地块
 * 永远拖不到 UI 上方，看起来就像「棋子消失了」。
 * 上下各留一点可拖动的余量（左右不动，避免影响水平居中）。
 */
const MAP_EDGE_PADDING = 200;

interface WDMapControllerProps {
  units: Unit[];
  phase: string;
  orders: IOrderDataHistorical[];
  maps: GameStateMaps;
  territories: APITerritories;
  centersByProvince: { [key: string]: { ownerCountryID: string } };
  standoffs: StandoffInfo[];
  isLivePhase: boolean; // Game is live and user is viewing the latest phase?
}

const WDMapController: React.FC<WDMapControllerProps> = function ({
  units,
  phase,
  orders,
  maps,
  territories,
  centersByProvince,
  isLivePhase,
  standoffs,
}): React.ReactElement {
  const svgElement = React.useRef<SVGSVGElement>(null);
  const [viewport] = useViewport();
  const dispatch = useAppDispatch();
  const ordersMeta = useAppSelector(gameOrdersMeta);
  const device = getDevice(viewport);
  const activeMapVariant = getActiveMapVariant();
  const {
    key: activeMapVariantKey,
    width: mapOriginalWidth,
    height: mapOriginalHeight,
  } = activeMapVariant;
  const [deviceScaleMin, scaleMax] = getInitialScaleForDevice(device);
  // 地图可指定初始缩放：小画布/文字密集的地图不再强行缩到一屏
  const initialScale = Math.min(
    scaleMax,
    Math.max(deviceScaleMin, activeMapVariant.initialScale ?? 0),
  );

  if (phase === "Error") return <div />;
  // const legalOrders = useAppSelector(gameLegalOrders);
  // console.log({ legalOrders });

  React.useLayoutEffect(() => {
    if (svgElement.current) {
      const fullMap = d3.select(svgElement.current);
      const contained = fullMap.select("#container");
      const containedRect = contained.node().getBBox();
      const gameBoardAreaRect = fullMap
        .select("#playableProvinces")
        .node()
        .getBBox();

      const { scale, x, y } = getInitialViewTranslation(
        containedRect,
        gameBoardAreaRect,
        initialScale,
        viewport,
      );

      const zoom = ({ transform }) => {
        contained.attr("transform", transform);
      };

      const d3Zoom = d3
        .zoom()
        .translateExtent([
          [-MAP_EDGE_PADDING, -MAP_EDGE_PADDING],
          [
            mapOriginalWidth + MAP_EDGE_PADDING,
            mapOriginalHeight + MAP_EDGE_PADDING,
          ],
        ])
        // 允许缩小到设备下限（手机上是 0.32），这样竖屏时能一眼看到整张地图；
        // 初始视角仍由 initialScale 决定，不会一进来就是缩小的。
        .scaleExtent([deviceScaleMin, scaleMax])
        .clickDistance(3)
        .on("zoom", zoom);

      fullMap
        .on("wheel", (e) => e.preventDefault())
        .call(d3Zoom)
        .call(d3Zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale))
        .on("dblclick.zoom", null);
    }
  }, [
    svgElement,
    viewport,
    activeMapVariantKey,
    mapOriginalWidth,
    mapOriginalHeight,
  ]);

  React.useEffect(() => {
    setTimeout(() => {
      dispatch(gameApiSliceActions.updateOrdersMeta(ordersMeta));
    }, 500);
  }, []);

  React.useEffect(() => {
    const keydownHandler = (e) => {
      const keyCode = e.which || e.keyCode;
      const ESCAPE = 27;
      // console.log("KEYCODE");
      // console.log(keyCode);
      if (keyCode === ESCAPE) {
        e.preventDefault();
        // console.log("DISPATCH RESET ORDER");
        dispatch(gameApiSliceActions.resetOrder());
      }
    };
    // console.log("ADDING HANLDER");
    window.addEventListener("keydown", keydownHandler);
    return () => window.removeEventListener("keydown", keydownHandler);
  });

  return (
    <div
      id="map-container"
      style={{
        width: viewport.width,
        height: viewport.height,
        // 平移可以略微超出地图边界（见 MAP_EDGE_PADDING）；底色取海面蓝，
        // 拖到边界外时不会露出突兀的白色空白。
        backgroundColor: "#8FB3D9",
      }}
    >
      <WDMap
        ref={svgElement}
        units={units}
        phase={phase}
        orders={orders}
        maps={maps}
        territories={territories}
        centersByProvince={centersByProvince}
        standoffs={standoffs}
        isLivePhase={isLivePhase}
      />
    </div>
  );
};

export default WDMapController;
